// utils/criteriaUtils.js
import { processValuesByOperation } from '../../../../../utils/goalProgressUtils';
import { PROMPTING_LEVEL_LABELS } from '../../../../../constants/GoalsConstants';

export const formatTargetValue = (value) => {
  if (!value) return '';
  if (value.toLowerCase().includes('most')) return 'At most';
  if (value.toLowerCase().includes('least')) return 'At least';
  return value;
};


export const getVariableLabel = (criteria) => {
  if (!criteria) return '';
  switch (criteria.MeasurementType) {
    case 'Duration':
      return `minutes of ${criteria.Variable}`;
    case 'Frequency':
      return `times of ${criteria.Variable}`;
    case 'Percentage':
      return `% of ${criteria.Variable}`;
    case 'Prompting':
      if (criteria.PromptingLevel) {
        return PROMPTING_LEVEL_LABELS[criteria.PromptingLevel] || criteria.PromptingLevel;
      }
      return criteria.Variable;
    default:
      return criteria.Variable;
  }
};


export const getCriteriaVariableLabel = (criteria) => {
  if (!criteria) return '';
  if (criteria.MeasurementType === 'Prompting' && criteria.PromptingLevel) {
    return PROMPTING_LEVEL_LABELS[criteria.PromptingLevel] || criteria.PromptingLevel;
  }
  return criteria.Variable || criteria.variable || '';
};

export const getCriteriaOptions = (
  goalData,
  typeOfGoal,
  promptingOptions,
  studentDataPoints
) => {
  const options = [];
  if (!goalData) return options;

  // Main goal criteria
  goalData.DataCollection?.measurementType?.Criteria?.forEach(
    (criteria, idx) => {
      const label = `Main - Criteria #${idx + 1}: ${formatTargetValue(
        criteria.Target
      )} ${criteria.TargetDuration} ${getVariableLabel(criteria)}`;
      options.push({
        value: `main-${idx}`,
        label,
        data: criteria,
        goalType: 'Main',
        criteriaIndex: idx,
      });
    }
  );

  // Subgoal criteria
  goalData.HasSubgoal?.Subgoals?.forEach((subgoal, subgoalIdx) => {
    subgoal.Criteria?.forEach((criteria, critIdx) => {
      const label = `Subgoal #${subgoalIdx + 1} - Criteria #${
        critIdx + 1
      }: ${formatTargetValue(criteria.Target)} ${
        criteria.TargetDuration
      } ${getVariableLabel(criteria)}`;
      options.push({
        value: `sub-${subgoalIdx}-${critIdx}`,
        label,
        data: criteria,
        goalType: 'Subgoal',
        subgoalIndex: subgoalIdx, // 0-based
        criteriaIndex: critIdx,
      });
    });
  });

  return options;
};

export const processTimeSeriesData = (
  selectedOption,
  studentDataPoints,
  multipleDataPointsOperation
) => {
  const timeSeriesData = [];
  if (!studentDataPoints || !Array.isArray(studentDataPoints))
    return timeSeriesData;

  const isSubgoal = selectedOption.goalType === 'Subgoal';
  const targetSubgoalIndex = isSubgoal ? selectedOption.subgoalIndex : null;
  const targetCriteriaIndex = selectedOption.criteriaIndex;

  const dateDataMap = {};

  studentDataPoints.forEach((studentDataPoint) => {
    const entry = studentDataPoint.dataPoints;
    if (!entry) return;

    const date = new Date(entry.createdAt._seconds * 1000);
    const dateKey = date.toLocaleDateString();

    const dataPoints = Array.isArray(entry?.info?.data_points)
      ? entry.info.data_points
      : entry?.info?.data_points
      ? [entry.info.data_points]
      : [];

    dataPoints.forEach((dataPoint) => {
      const criteriaArray = Array.isArray(dataPoint.criteria)
        ? dataPoint.criteria
        : dataPoint.criteria
        ? [dataPoint.criteria]
        : [];

      criteriaArray.forEach((criteriaData, dataPointCriteriaIndex) => {
        let includeDataPoint = false;

        if (isSubgoal) {
          const currentGoalStr = (dataPoint.currentGoal ?? 'main').toString();
          includeDataPoint =
            criteriaData.Variable === selectedOption.data.Variable &&
            currentGoalStr === targetSubgoalIndex.toString() &&
            dataPointCriteriaIndex === targetCriteriaIndex;
        } else {
          const isMainGoal =
            !dataPoint.currentGoal || dataPoint.currentGoal === 'main';
          includeDataPoint =
            criteriaData.Variable === selectedOption.data.Variable &&
            isMainGoal &&
            dataPointCriteriaIndex === targetCriteriaIndex;
        }

        if (!includeDataPoint) return;

        if (!dateDataMap[dateKey]) {
          dateDataMap[dateKey] = {
            values: [],
            goalValues: [],
            trialsArrays: [],
            timestamp: entry.createdAt._seconds,
            targetDuration:
              Number.parseFloat(selectedOption.data.TargetDuration) || 0,
            variable: criteriaData.Variable,
            measurementType: criteriaData.MeasurementType || '',
            target: formatTargetValue(
              selectedOption.data.Target || criteriaData.Target
            ),
            promptingMeasurement:
              dataPoint.prompting_measurement ||
              dataPoint.promptingMeasurement ||
              '',
            subgoalInfo: {
              isSubgoal,
              targetSubgoalIndex,
              targetCriteriaIndex,
              dataPointCurrentGoal: dataPoint.currentGoal,
              dataPointCriteriaIndex,
            },
          };
        }

        dateDataMap[dateKey].values.push(
          Number.parseFloat(criteriaData.value) || 0
        );
        dateDataMap[dateKey].goalValues.push(
          Number.parseFloat(dataPoint.goal_value) || 0
        );

        if (dataPoint.trialsNumber && Array.isArray(dataPoint.trialsNumber)) {
          dateDataMap[dateKey].trialsArrays.push(dataPoint.trialsNumber);
        } else {
          dateDataMap[dateKey].trialsArrays.push([]);
        }
      });
    });
  });

  Object.keys(dateDataMap).forEach((dateKey) => {
    const dateData = dateDataMap[dateKey];
    if (dateData.values.length === 0) return;

    const processedValue =
      multipleDataPointsOperation && dateData.values.length > 1
        ? processValuesByOperation(dateData.values, multipleDataPointsOperation)
        : dateData.values[0];

    const processedGoalValue =
      multipleDataPointsOperation && dateData.goalValues.length > 1
        ? processValuesByOperation(
            dateData.goalValues,
            multipleDataPointsOperation
          )
        : dateData.goalValues[0] || 0;

    // Trials processing
    let processedTrialsNumber = [];
    if (dateData.trialsArrays.length > 0) {
      const averagedTrials = dateData.trialsArrays.map((arr) => {
        if (!arr || arr.length === 0) return 0;
        const nums = arr
          .map((v) => Number.parseFloat(v))
          .filter((v) => !isNaN(v));
        return nums.length > 0
          ? nums.reduce((a, b) => a + b, 0) / nums.length
          : 0;
      });
      processedTrialsNumber =
        multipleDataPointsOperation && averagedTrials.length > 1
          ? [
              processValuesByOperation(
                averagedTrials,
                multipleDataPointsOperation
              ),
            ]
          : [averagedTrials[0] || 0];
    }

    timeSeriesData.push({
      date: dateKey,
      timestamp: dateData.timestamp,
      value: processedValue,
      targetDuration: dateData.targetDuration,
      variable: dateData.variable,
      measurementType: dateData.measurementType,
      target: dateData.target,
      trialsNumber: processedTrialsNumber,
      goal_value: processedGoalValue,
      subgoalInfo: dateData.subgoalInfo,
      operation: multipleDataPointsOperation || 'None',
      originalValues: dateData.values,
      originalGoalValues: dateData.goalValues,
      originalTrialsArrays: dateData.trialsArrays,
      valuesCount: dateData.values.length,
      goalValuesCount: dateData.goalValues.length,
      trialsArraysCount: dateData.trialsArrays.length,
      promptingMeasurement: dateData.promptingMeasurement || '',
    });
  });

  return timeSeriesData.sort((a, b) => a.timestamp - b.timestamp);
};
